import { Chart as ChartJS, Filler, LineElement, PointElement, RadialLinearScale, Tooltip } from 'chart.js'
import { FC, useMemo } from 'react'
import { Radar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { useRadarChartOptions } from '../../helpers/chartTheme'
import { getMotivationChartHeight } from '../../helpers/motivationChartHeight'
import {
  DASHED_MOTIVATION_SOURCES,
  MOTIVATION_DASH_PATTERN,
  MOTIVATION_SOURCE_LABEL_KEYS,
  MOTIVATION_SOURCE_ORDER,
  useMotivationSourceColors,
} from '../../helpers/motivationSourceColors'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { MotivationScoresBySource, MotivationSource } from '../../types'

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip)

interface MotivationRadarChartProps {
  motivationScores: MotivationScoresBySource
  /** Sources whose dataset should be hidden (driven by the shared legend). */
  hiddenSources?: ReadonlySet<MotivationSource>
}

export const MotivationRadarChart: FC<MotivationRadarChartProps> = ({ motivationScores, hiddenSources }) => {
  const { t } = useTranslation('ua-insights')
  const sourceColors = useMotivationSourceColors()
  const { motivationLabel } = useUATaxonomy()

  const labels = useMemo(() => motivationScores.creativeAds.map((s) => motivationLabel(s.motivation)), [motivationScores, motivationLabel])

  const chartData = useMemo(
    () => ({
      labels,
      // Drop sources whose data is missing for the current game (e.g. no
      // Game & Live Events profile) instead of rendering a degenerate
      // polygon — an empty/zeroed series under a real-looking label would
      // misrepresent which signals are actually available.
      datasets: MOTIVATION_SOURCE_ORDER.flatMap((source) => {
        const series = motivationScores[source]
        if (!series) return []
        const colors = sourceColors[source]
        const isDashed = DASHED_MOTIVATION_SOURCES.has(source)
        return [
          {
            label: t(MOTIVATION_SOURCE_LABEL_KEYS[source]),
            data: series.map((s) => s.avgIntensity),
            backgroundColor: colors.fill,
            borderColor: colors.stroke,
            // Small filled dot, no border ring – removes the chunky look the
            // previous white-bordered dots produced on dense radar polygons.
            pointBackgroundColor: colors.stroke,
            pointBorderColor: colors.stroke,
            pointBorderWidth: 0,
            pointHoverBackgroundColor: colors.stroke,
            pointHoverBorderColor: colors.stroke,
            pointHoverBorderWidth: 0,
            pointHoverRadius: 4,
            borderWidth: 2,
            pointRadius: 2,
            // Tooltip uses `usePointStyle` to render circular swatches; setting
            // `pointStyle` here keeps the lookup explicit instead of relying on
            // Chart.js' radar default.
            pointStyle: 'circle' as const,
            fill: true,
            borderDash: isDashed ? [...MOTIVATION_DASH_PATTERN] : [],
            hidden: hiddenSources?.has(source) ?? false,
          },
        ]
      }),
    }),
    [labels, motivationScores, sourceColors, t, hiddenSources]
  )

  const options = useRadarChartOptions()

  // Match the bar chart's per-motivation scaling so the two side-by-side
  // charts stay vertically aligned; the radar polygon is bounded by the
  // smaller of width/height anyway, so the extra vertical room is harmless.
  return (
    <Box sx={{ height: getMotivationChartHeight(motivationScores.creativeAds.length), position: 'relative' }}>
      <Radar data={chartData} options={options} />
    </Box>
  )
}
